package link.erpango.quip.domain.extraction

/** Max distance (chars) from a vehicle anchor to associate a spare part / maintenance with it. */
private const val ASSOCIATION_WINDOW = 200

/** Max gap between two anchors to merge them into the same vehicle cluster. */
private const val ANCHOR_MERGE_DISTANCE = 120

/** Max distance (chars) from the VIN anchor to accept a commercial model token as its model. */
private const val MODEL_FALLBACK_DISTANCE = 200

/** Search window (chars) around a brand mention for its model name. */
private const val MODEL_WINDOW = 60

/** Year within this distance of a brand/model mention -> HIGH confidence. */
private const val YEAR_NEAR = 24

/** Year within this distance of a brand/model mention -> MEDIUM confidence. */
private const val YEAR_FAR = 60

/** Window (chars) around an oil spec to look for a known oil brand word. */
private const val OIL_BRAND_WINDOW = 40

/** Window (chars) around a spare-part phrase to look for "ref." markers. */
private const val REFERENCE_WINDOW = 30

/** Window (chars) around a maintenance action to look for date / km / cost. */
private const val MAINTENANCE_FIELD_WINDOW = 80

/**
 * A maintenance-log row is a short line holding a km value ("59.960 Km - 4/7/2021"); prose
 * sentences also mention kms but run much longer, so only short lines count towards log mode.
 */
private const val LOG_ROW_MAX_LENGTH = 80

/** Window (chars) before a part phrase to look for its quantity ("4 bujías"). */
private const val QUANTITY_WINDOW = 24

/** Window (chars) after a part phrase to look for its quantity ("... 2 ud"). */
private const val QUANTITY_AFTER_WINDOW = 40

/**
 * Parses a pasted note offline into candidate vehicles, spare parts and maintenances, each
 * field tagged with a confidence and the source span in the original text.
 *
 * Pure function: no DB, network, DI or LLM involved. The caller passes the catalog data it
 * wants to match against ([brands] and [modelsByBrand]); everything else is detected with
 * deterministic rules (VIN checksum, Spanish plate formats, keyword tables). Any content in
 * the note is treated as plain text: nothing in here interprets or executes it.
 *
 * Association heuristic: VIN / plate / brand mentions act as anchors; anchors close together
 * (gap <= [ANCHOR_MERGE_DISTANCE], unless they are the same kind with different values) form
 * one vehicle. Spare parts and maintenances are assigned to the nearest anchor within
 * [ASSOCIATION_WINDOW]; anything farther away goes to the unassigned bucket ("asignar después").
 */
fun parseNote(
    text: String,
    brands: List<String>,
    modelsByBrand: Map<String, List<String>>,
): ExtractionResult {
    if (text.isBlank()) return ExtractionResult(emptyList(), emptyList(), emptyList())
    return LocalNoteParser(text, normalizeText(text), brands, modelsByBrand).parse()
}

/** Accented characters mapped to their unaccented form (Spanish profile). */
private val ACCENT_MAP: Map<Char, Char> = mapOf(
    'á' to 'a', 'é' to 'e', 'í' to 'i', 'ó' to 'o', 'ú' to 'u', 'ü' to 'u', 'ñ' to 'n',
    'Á' to 'A', 'É' to 'E', 'Í' to 'I', 'Ó' to 'O', 'Ú' to 'U', 'Ü' to 'U', 'Ñ' to 'N'
)

/**
 * Normalizes text for matching (lowercase + accent removal) keeping a 1:1 character mapping so
 * offsets in the normalized string are valid offsets in the original one. Covers the Spanish
 * accented characters; any other Unicode passes through lowercased as-is.
 */
internal fun normalizeText(text: String): String = buildString(text.length) {
    for (c in text) append(ACCENT_MAP[c] ?: c.lowercaseChar())
}

/** ISO 3779 VIN checksum validation: the check character is at position 9 (index 8). */
internal fun isValidVinChecksum(vin: String): Boolean {
    if (vin.length != 17) return false
    var sum = 0
    for (i in 0 until 17) {
        if (i == 8) continue // position 9 is the checksum itself
        val value = VIN_CHAR_VALUES[vin[i].uppercaseChar()] ?: return false
        sum += value * VIN_WEIGHTS[i - if (i > 8) 1 else 0]
    }
    val check = sum % 11
    val expected = if (check == 10) 'X' else ('0' + check)
    return vin[8].uppercaseChar() == expected
}

/**
 * Light "OCR mode" normalization for values extracted from a line: exotic spaces and tabs become
 * a single regular space and runs collapse. It only touches whitespace, so applying it to already
 * clean pasted text is a no-op. It is *not* applied to the whole note (that would break offsets);
 * it is used on label values and identifiers.
 */
internal fun normalizeOcrText(text: String): String = buildString(text.length) {
    var lastWasSpace = false
    for (c in text) {
        val ch = if (c == '\t' || c == '\u00A0' || c == '\u2007' || c == '\u202F') ' ' else c
        if (ch == ' ') {
            if (!lastWasSpace) append(ch)
            lastWasSpace = true
        } else {
            append(ch)
            lastWasSpace = false
        }
    }
}.trim()

/** OCR commonly confuses these letters with digits; the map gives the digit they usually stand for. */
private val OCR_LETTER_TO_DIGIT = mapOf(
    'O' to '0', 'Q' to '0', 'D' to '0', 'I' to '1', 'L' to '1',
    'Z' to '2', 'S' to '5', 'G' to '6', 'B' to '8',
)

/** And these digits with letters, used to repair a plate's letter block. */
private val OCR_DIGIT_TO_LETTER = mapOf(
    '0' to 'O', '1' to 'I', '5' to 'S', '8' to 'B', '2' to 'Z', '6' to 'G',
)

/** Keeps only letters and digits so an identifier split by OCR ("4587 KLM") rejoins. */
internal fun cleanIdentifier(raw: String): String =
    normalizeOcrText(raw).uppercase().filter { it.isLetterOrDigit() }

/**
 * Recovers a checksum-valid VIN from an OCR-noisy candidate. First maps the characters that are
 * never valid in a VIN (I, O, Q) to their likely digits, then tries a single S/5, B/8, Z/2, G/6,
 * D/0 or L/1 substitution. Returns null when the raw value is valid or nothing repairs it, so a
 * clean VIN is never downgraded and a repair is always reported at lower confidence.
 */
internal fun repairVin(raw: String): String? {
    val cleaned = cleanIdentifier(raw)
    if (cleaned.length != 17) return null
    if (isValidVinChecksum(cleaned)) return cleaned
    val invalidFixed = buildString(17) {
        for (c in cleaned) append(when (c) { 'I' -> '1'; 'O' -> '0'; 'Q' -> '0'; else -> c })
    }
    if (isValidVinChecksum(invalidFixed)) return invalidFixed
    for (i in invalidFixed.indices) {
        val replacement = OCR_LETTER_TO_DIGIT[invalidFixed[i]] ?: continue
        val candidate = invalidFixed.substring(0, i) + replacement + invalidFixed.substring(i + 1)
        if (isValidVinChecksum(candidate)) return candidate
    }
    return null
}

/** Parses bounded Spanish number words ("cuarenta mil", "doscientos treinta y cuatro"). */
internal fun parseSpanishNumberWords(tokens: List<String>): Int? {
    if (tokens.isEmpty()) return null
    val milIndex = tokens.lastIndexOf("mil")
    return if (milIndex >= 0) {
        val highTokens = tokens.subList(0, milIndex)
        val lowTokens = tokens.subList(milIndex + 1, tokens.size)
        val high = if (highTokens.isEmpty()) 1 else parseSimpleNumberWords(highTokens) ?: return null
        if (high > 999) return null
        val low = if (lowTokens.isEmpty()) 0 else parseSimpleNumberWords(lowTokens) ?: return null
        high * 1000 + low
    } else {
        parseSimpleNumberWords(tokens)
    }
}

private fun parseSimpleNumberWords(tokens: List<String>): Int? {
    var total = 0
    var i = 0
    if (i < tokens.size && HUNDRED_WORDS.containsKey(tokens[i])) {
        total += HUNDRED_WORDS[tokens[i]]!!
        i++
    }
    if (i >= tokens.size) return total.takeIf { it > 0 }
    val next = tokens[i]
    TENS_WORDS[next]?.let { tens ->
        total += tens
        i++
        if (i + 1 < tokens.size && tokens[i] == "y") {
            val unit = UNIT_WORDS[tokens[i + 1]] ?: return null
            total += unit
            i += 2
        } else if (i < tokens.size) {
            val unit = UNIT_WORDS[tokens[i]] ?: return null
            total += unit
            i++
        }
    } ?: run {
        total += UNIT_WORDS[next] ?: return null
        i++
    }
    if (i != tokens.size) return null
    return total.takeIf { it > 0 }
}

private val UNIT_WORDS = mapOf(
    "cero" to 0, "un" to 1, "una" to 1, "uno" to 1, "dos" to 2, "tres" to 3, "cuatro" to 4,
    "cinco" to 5, "seis" to 6, "siete" to 7, "ocho" to 8, "nueve" to 9, "diez" to 10,
    "once" to 11, "doce" to 12, "trece" to 13, "catorce" to 14, "quince" to 15,
    "dieciseis" to 16, "diecisiete" to 17, "dieciocho" to 18, "diecinueve" to 19,
    "veinte" to 20, "veintiuno" to 21, "veintidos" to 22, "veintitres" to 23,
    "veinticuatro" to 24, "veinticinco" to 25, "veintiseis" to 26, "veintisiete" to 27,
    "veintiocho" to 28, "veintinueve" to 29
)

private val TENS_WORDS = mapOf(
    "treinta" to 30, "cuarenta" to 40, "cincuenta" to 50, "sesenta" to 60,
    "setenta" to 70, "ochenta" to 80, "noventa" to 90
)

private val HUNDRED_WORDS = mapOf(
    "cien" to 100, "ciento" to 100, "doscientos" to 200, "trescientos" to 300,
    "cuatrocientos" to 400, "quinientos" to 500, "seiscientos" to 600,
    "setecientos" to 700, "ochocientos" to 800, "novecientos" to 900
)

/**
 * Field a `Etiqueta: valor` line can fill. One normalized-label table serves both document
 * families: vehicle data sheets and workshop invoices.
 */
private enum class LabelledField {
    VIN, PLATE, BRAND, MODEL, FIRST_REGISTRATION, NEXT_ITV, FUEL, CYLINDERS, DISPLACEMENT,
    OIL, TIRE_SIZE, TIRE_PRESSURE_FRONT, TIRE_PRESSURE_REAR,
    KILOMETERS, DATE, WORKSHOP, MECHANIC, TOTAL, TAX_BASE, VAT,
}

/** One `Etiqueta: valor` pair found in the note. [span] covers the value only, not the label. */
private data class LabelledValue(
    val field: LabelledField,
    val label: String,
    val rawValue: String,
    val span: SourceSpan,
    /** 1 = ">160 km/h", -1 = "<160 km/h", null = the label did not state a speed range. */
    val speedQualifier: Int? = null,
)

/**
 * Maps a normalized (lowercase, punctuation-free) label to the field it names. Order matters:
 * the most specific spellings are checked first ("fecha de matriculación" before "matrícula",
 * "ITV" before a generic "fecha").
 */
private fun matchLabelledField(label: String): LabelledField? = when {
    "itv" in label -> LabelledField.NEXT_ITV
    "fecha" in label && ("matricul" in label || "alta" in label || "primera" in label) -> LabelledField.FIRST_REGISTRATION
    "matricul" in label -> LabelledField.PLATE
    "bastidor" in label || "chasis" in label || "vin" in label -> LabelledField.VIN
    "cilindrada" in label -> LabelledField.DISPLACEMENT
    "cilindro" in label -> LabelledField.CYLINDERS
    "combustible" in label -> LabelledField.FUEL
    "marca" in label -> LabelledField.BRAND
    "modelo" in label -> LabelledField.MODEL
    "presion" in label && "delant" in label -> LabelledField.TIRE_PRESSURE_FRONT
    "presion" in label && "trase" in label -> LabelledField.TIRE_PRESSURE_REAR
    "presion" in label -> null // "presión de neumáticos" without a position is ambiguous
    "neumatic" in label -> LabelledField.TIRE_SIZE
    "aceite" in label -> LabelledField.OIL
    "taller" in label -> LabelledField.WORKSHOP
    "mecanic" in label -> LabelledField.MECHANIC
    "kilometr" in label || "odometr" in label || "km" in label -> LabelledField.KILOMETERS
    "base" in label || "subtotal" in label || "sin iva" in label -> LabelledField.TAX_BASE
    "total" in label -> LabelledField.TOTAL
    "iva" in label -> LabelledField.VAT
    "fecha" in label -> LabelledField.DATE
    else -> null
}

private class LocalNoteParser(
    private val text: String,
    private val norm: String,
    private val brands: List<String>,
    modelsByBrand: Map<String, List<String>>,
) {

    /** Catalog models keyed by normalized brand name for lenient lookup. */
    private val normalizedModels: Map<String, List<Pair<String, String>>> = modelsByBrand.entries
        .groupBy { normalizeText(it.key) }
        .mapValues { (_, entries) ->
            entries.flatMap { entry -> entry.value.map { model -> model to normalizeText(model) } }
        }

    private data class Anchor(
        val span: SourceSpan,
        val kind: Kind,
        val value: String?,
        val confidence: ExtractionConfidence = ExtractionConfidence.HIGH,
    ) {
        enum class Kind { VIN, PLATE, BRAND }
    }

    /** A detected field occurrence waiting to be assigned to a vehicle cluster. */
    private data class Occurrence(
        val span: SourceSpan,
        val value: String,
        val confidence: ExtractionConfidence = ExtractionConfidence.MEDIUM,
    ) {
        fun toField(): ExtractedField<String> = ExtractedField(value, confidence, span)
    }

    /** Which end of the vehicle a bulb code was written for; null when the note does not say. */
    private enum class BulbSide { FRONT, REAR }

    /** A bulb code with the side it applies to ("Bombillas delanteras: H7"). */
    private data class BulbMention(val occurrence: Occurrence, val side: BulbSide?)

    private class Cluster(val anchors: MutableList<Anchor> = mutableListOf())

    /** Per-vehicle field occurrences detected globally, assigned to clusters greedily. */
    private data class VehicleFields(
        val oilSpecs: List<Occurrence>,
        val tireSizes: List<Occurrence>,
        val fuelTypes: List<Occurrence>,
        val vehicleTypes: List<Occurrence>,
        val engineDetails: List<Occurrence>,
        // Explicit "Modelo:" values, used when the brand is not in the catalog.
        val labelledModels: List<Occurrence> = emptyList(),
        val cylinderCounts: List<Occurrence> = emptyList(),
        val frontPressuresBelow160: List<Occurrence> = emptyList(),
        val rearPressuresBelow160: List<Occurrence> = emptyList(),
        val frontPressuresAbove160: List<Occurrence> = emptyList(),
        val rearPressuresAbove160: List<Occurrence> = emptyList(),
        val firstRegistrations: List<Occurrence> = emptyList(),
        val nextItvs: List<Occurrence> = emptyList(),
        val frontBulbs: List<Occurrence> = emptyList(),
        val rearBulbs: List<Occurrence> = emptyList(),
        /** Bulb codes written without a side; suggested for both ends. */
        val unsidedBulbs: List<Occurrence> = emptyList(),
        val batterySpecs: List<Occurrence> = emptyList(),
        val coolants: List<Occurrence> = emptyList(),
    )

    private val usedSpans = mutableSetOf<SourceSpan>()

    /** Substring of this string covered by [span]. */
    private fun String.substring(span: SourceSpan): String = substring(span.start, span.end)

    fun parse(): ExtractionResult {
        val labelled = labelledFields
        val isInvoice = looksLikeInvoice()
        val isLog = !isInvoice && looksLikeLog()

        // 1. Anchors: VINs and plates first (plate candidates overlapping a VIN are dropped).
        // Label-derived anchors are merged in and deduplicated so a clean regex match wins.
        val regexVinAnchors = findVinCandidates().map { Anchor(it.span, Anchor.Kind.VIN, it.value, it.confidence) }
        val regexPlateAnchors = findPlateSpans()
            .map { Anchor(it, Anchor.Kind.PLATE, text.substring(it).uppercase(), ExtractionConfidence.HIGH) }
        val vinAnchors = dedupeAnchors(regexVinAnchors + labelled.vinAnchors)
        val plateAnchors = dedupeAnchors(regexPlateAnchors + labelled.plateAnchors)
            .filter { plate -> vinAnchors.none { it.span.overlaps(plate.span) } }
        val brandAnchors = dedupeAnchors(findBrandMentions() + labelled.brandAnchors)

        // 2. Cluster anchors into vehicles (greedy by position).
        // A single VIN plus at most one plate unambiguously identify one vehicle, even when its
        // anchors are scattered far apart on an ITV card / data sheet. Such documents also repeat
        // the brand several times, so the distance-based clustering would split them into several
        // phantom vehicles. Merge everything in that case; otherwise keep the multi-vehicle logic.
        val clusters = if (vinAnchors.size == 1 && plateAnchors.size <= 1) {
            listOf(Cluster((vinAnchors + plateAnchors + brandAnchors).sortedBy { it.span.start }.toMutableList()))
        } else {
            clusterAnchors(vinAnchors + plateAnchors + brandAnchors)
        }

        // 3. Global field occurrences, assigned to the nearest cluster below.
        val bulbMentions = findBulbMentions()
        val fields = VehicleFields(
            oilSpecs = findOilSpecs() + labelled.oilSpecs,
            // Uppercase the rim letter ("205/55 R16") for a consistent canonical form.
            tireSizes = findTireSizes().map { Occurrence(it, text.substring(it).uppercase()) } + labelled.tireSizes,
            fuelTypes = findFuelTypes() + labelled.fuelTypes,
            vehicleTypes = findVehicleTypes(),
            engineDetails = findEngineDetails() + labelled.engineDetails,
            labelledModels = labelled.models,
            cylinderCounts = labelled.cylinderCounts,
            frontPressuresBelow160 = labelled.frontPressuresBelow160,
            rearPressuresBelow160 = labelled.rearPressuresBelow160,
            frontPressuresAbove160 = labelled.frontPressuresAbove160,
            rearPressuresAbove160 = labelled.rearPressuresAbove160,
            firstRegistrations = labelled.firstRegistrations,
            nextItvs = labelled.nextItvs + findNextItvMentions(),
            frontBulbs = bulbMentions.filter { it.side == BulbSide.FRONT }.map { it.occurrence },
            rearBulbs = bulbMentions.filter { it.side == BulbSide.REAR }.map { it.occurrence },
            unsidedBulbs = bulbMentions.filter { it.side == null }.map { it.occurrence },
            batterySpecs = findBatterySpecs(),
            coolants = findCoolants(),
        )

        // 4. One CandidateVehicle per cluster (clusters are in text order).
        var vehicles = clusters.map { buildVehicle(it, fields) }
        // A one-vehicle note may state "Próxima <date>" far from the vehicle anchors; with a single
        // cluster there is no ambiguity, so keep it instead of dropping it as unassigned.
        if (clusters.size == 1 && vehicles[0].nextItvDate == null) {
            fields.nextItvs.firstOrNull()?.let { nextItv ->
                vehicles = listOf(vehicles[0].copy(nextItvDate = nextItv.toField()))
            }
        }

        // 5. Spare parts and maintenances with vehicle association.
        val anchorAssignments = clusters.flatMapIndexed { index, cluster ->
            cluster.anchors.map { it.span to index }
        }
        // Text order (not keyword-length order) so the review UI lists items as written and
        // each part's reference search can stop at the next part in reading order.
        val partMatches = findSparePartMatches().sortedBy { it.span.start }
        val spareParts = partMatches.mapIndexed { index, match ->
            // On an invoice with a single identified vehicle, every line belongs to it even if
            // the total sits far from the header; otherwise use the usual proximity rule.
            val vehicleIndex = if (isInvoice && clusters.size == 1) 0
            else nearestVehicleIndex(anchorAssignments, match.span)
            buildSparePart(
                match = match,
                vehicleIndex = vehicleIndex,
                nextPartSpan = partMatches.getOrNull(index + 1)?.span,
                quantity = if (isInvoice) findQuantityNear(match.span, partMatches.getOrNull(index + 1)?.span) else null,
            )
        }
        val maintenances = when {
            // A workshop document is one maintenance: its date/km/total header, not one action.
            isInvoice -> listOfNotNull(buildInvoiceMaintenance(anchorAssignments, clusters.size, partMatches))
            // A maintenance log is one maintenance per (km, date) row, keyed by its section header.
            isLog -> buildLogMaintenances(anchorAssignments, clusters.size)
            else -> findMaintenanceActions()
                .filter { hasMaintainableField(it) }
                .sortedBy { it.span.start }
                .map { buildMaintenance(it, nearestVehicleIndex(anchorAssignments, it.span)) }
        }

        return ExtractionResult(vehicles, spareParts, maintenances)
    }

    /** Drops anchors of the same kind whose spans overlap (longest/first match wins). */
    private fun dedupeAnchors(anchors: List<Anchor>): List<Anchor> {
        val result = mutableListOf<Anchor>()
        for (anchor in anchors) {
            if (result.any { it.kind == anchor.kind && it.span.overlaps(anchor.span) }) continue
            result += anchor
        }
        return result
    }

    // --- Vehicle construction -------------------------------------------------------------

    private fun clusterAnchors(anchors: List<Anchor>): List<Cluster> {
        val sorted = anchors.sortedBy { it.span.start }
        val clusters = mutableListOf<Cluster>()
        for (anchor in sorted) {
            val current = clusters.lastOrNull()
            if (current != null && shouldMerge(current, anchor)) {
                current.anchors += anchor
            } else {
                clusters += Cluster(mutableListOf(anchor))
            }
        }
        return clusters
    }

    private fun shouldMerge(cluster: Cluster, anchor: Anchor): Boolean {
        val last = cluster.anchors.last()
        if (anchor.span.start - last.span.end > ANCHOR_MERGE_DISTANCE) return false
        // Same kind with a different value close together means two vehicles
        // ("...Toyota Corolla 3574JZT y mi Ford Focus 1234ABC").
        for (existing in cluster.anchors) {
            if (existing.kind == anchor.kind && existing.value != null && anchor.value != null &&
                existing.value != anchor.value && !existing.span.overlaps(anchor.span)
            ) return false
        }
        return true
    }

    private fun buildVehicle(cluster: Cluster, fields: VehicleFields): CandidateVehicle {
        val vinAnchor = cluster.anchors.firstOrNull { it.kind == Anchor.Kind.VIN }
        val vin = vinAnchor?.let { anchor ->
            // VINs are case-insensitive identifiers; normalize to uppercase for display and storage.
            ExtractedField(value = anchor.value!!, confidence = anchor.confidence, span = anchor.span)
        }
        val plateAnchor = cluster.anchors.firstOrNull { it.kind == Anchor.Kind.PLATE }
        val plate = plateAnchor?.let {
            ExtractedField(value = it.value!!, confidence = it.confidence, span = it.span)
        }
        val brandAnchor = cluster.anchors.firstOrNull { it.kind == Anchor.Kind.BRAND }
        val brand = brandAnchor?.let {
            // Canonical catalog name (not the note's casing) so creation can use it directly;
            // the span still points at where it was written in the note.
            ExtractedField(value = it.value!!, confidence = it.confidence, span = it.span)
        }

        // Model: nearest model name of the matched brand within MODEL_WINDOW of the mention.
        var model: ExtractedField<String>? = null
        if (brandAnchor != null) {
            val models = normalizedModels[normalizeText(brandAnchor.value!!)] ?: emptyList()
            for ((originalModel, normModel) in models) {
                if (normModel.isEmpty()) continue
                for (span in findOccurrences(normModel)) {
                    if (span.centerDistanceTo(brandAnchor.span) > MODEL_WINDOW) continue
                    // Canonical catalog name; the span keeps the note's original spelling.
                    val candidate = ExtractedField(value = originalModel, confidence = ExtractionConfidence.MEDIUM, span = span)
                    if (model == null || span.centerDistanceTo(brandAnchor.span) < model.span.centerDistanceTo(brandAnchor.span)) {
                        model = candidate
                    }
                }
            }
        }
        // Data sheets state the model explicitly even when the brand is not in the catalog.
        if (model == null) model = assignNearest(fields.labelledModels, cluster)?.toField()
        // ITV cards state the commercial name (field D.3) with no label, so fall back to the
        // model-shaped token sitting next to the VIN ("CBR500RA"). Only when a brand was read but
        // it is absent from the model catalog (otherwise the catalog lookup above is authoritative):
        // running it with no brand made a nearby spare-part reference ("OC534") become the model.
        if (model == null && brandAnchor != null &&
            !normalizedModels.containsKey(normalizeText(brandAnchor.value!!))
        ) {
            model = findModelFallback(vinAnchor)
        }

        // Year: nearest 4-digit year to the brand/model mention.
        val referenceSpans = listOfNotNull(brand?.span, model?.span)
        var year: ExtractedField<Int>? = null
        var bestYearDistance = Int.MAX_VALUE
        if (referenceSpans.isNotEmpty()) {
            for (yearSpan in findYears()) {
                val distance = referenceSpans.minOf { it.centerDistanceTo(yearSpan) }
                when {
                    distance <= YEAR_NEAR -> {
                        year = ExtractedField(value = text.substring(yearSpan).toInt(), confidence = ExtractionConfidence.HIGH, span = yearSpan)
                        break
                    }

                    distance <= YEAR_FAR && distance < bestYearDistance -> {
                        year = ExtractedField(value = text.substring(yearSpan).toInt(), confidence = ExtractionConfidence.MEDIUM, span = yearSpan)
                        bestYearDistance = distance
                    }
                }
            }
        }

        // Remaining fields: nearest unused occurrence within ASSOCIATION_WINDOW of any anchor.
        val oilSpec = assignNearest(fields.oilSpecs, cluster)?.toField()
        val tireSize = assignNearest(fields.tireSizes, cluster)?.toField()
        val fuelType = assignNearest(fields.fuelTypes, cluster)?.toField()
        val vehicleType = assignNearest(fields.vehicleTypes, cluster)?.toField()
        val engineDetail = assignNearest(fields.engineDetails, cluster)?.toField()
        // A single bulb code written without a side ("Bombillas HI2R") is suggested for both ends,
        // the same way a single tire size fills front and rear.
        var frontBulb = assignNearest(fields.frontBulbs, cluster)?.toField()
        var rearBulb = assignNearest(fields.rearBulbs, cluster)?.toField()
        if (frontBulb == null || rearBulb == null) {
            assignNearest(fields.unsidedBulbs, cluster)?.toField()?.let { unsided ->
                if (frontBulb == null) frontBulb = unsided
                if (rearBulb == null) rearBulb = unsided
            }
        }
        val batterySpec = assignNearest(fields.batterySpecs, cluster)?.toField()
        val coolant = assignNearest(fields.coolants, cluster)?.toField()
        val cylinderCount = assignNearest(fields.cylinderCounts, cluster)?.let { occ ->
            occ.value.toIntOrNull()?.let { ExtractedField(it, occ.confidence, occ.span) }
        }
        val frontPressureBelow = assignNearest(fields.frontPressuresBelow160, cluster)?.toDoubleField()
        val rearPressureBelow = assignNearest(fields.rearPressuresBelow160, cluster)?.toDoubleField()
        val frontPressureAbove = assignNearest(fields.frontPressuresAbove160, cluster)?.toDoubleField()
        val rearPressureAbove = assignNearest(fields.rearPressuresAbove160, cluster)?.toDoubleField()
        val firstRegistration = assignNearest(fields.firstRegistrations, cluster)?.toField()
        val nextItv = assignNearest(fields.nextItvs, cluster)?.toField()

        // A data sheet states the registration date; derive the model year from it when the
        // brand/model proximity heuristic found nothing (labels can sit several lines apart).
        if (year == null && firstRegistration != null) {
            YEAR_REGEX.find(firstRegistration.value)?.let { match ->
                val span = SourceSpan(
                    firstRegistration.span.start + match.range.first,
                    firstRegistration.span.start + match.range.last + 1,
                )
                year = ExtractedField(match.value.toInt(), ExtractionConfidence.MEDIUM, span)
            }
        }

        return CandidateVehicle(
            brand = brand,
            model = model,
            year = year,
            plate = plate,
            vin = vin,
            oilSpec = oilSpec,
            tireSize = tireSize,
            fuelType = fuelType,
            vehicleType = vehicleType,
            engineDetails = engineDetail,
            cylinderCount = cylinderCount,
            frontTirePressureBelow160 = frontPressureBelow,
            rearTirePressureBelow160 = rearPressureBelow,
            frontTirePressureAbove160 = frontPressureAbove,
            rearTirePressureAbove160 = rearPressureAbove,
            firstRegistrationDate = firstRegistration,
            nextItvDate = nextItv,
            frontBulbType = frontBulb,
            rearBulbType = rearBulb,
            batterySpecs = batterySpec,
            coolantType = coolant,
        )
    }

    private fun Occurrence.toDoubleField(): ExtractedField<Double>? =
        value.toDoubleOrNull()?.let { ExtractedField(it, confidence, span) }


    /** Assigns the nearest unused occurrence (within ASSOCIATION_WINDOW of any anchor) to a cluster. */
    private fun assignNearest(occurrences: List<Occurrence>, cluster: Cluster): Occurrence? {
        var best: Occurrence? = null
        var bestDistance = Int.MAX_VALUE
        for (occurrence in occurrences) {
            if (usedSpans.contains(occurrence.span)) continue
            val distance = cluster.anchors.minOf { it.span.centerDistanceTo(occurrence.span) }
            if (distance <= ASSOCIATION_WINDOW && distance < bestDistance) {
                best = occurrence
                bestDistance = distance
            }
        }
        best?.let { usedSpans += it.span }
        return best
    }

    // --- Anchor detection -------------------------------------------------------------------

    private fun findVinCandidates(): List<Occurrence> {
        val result = mutableListOf<Occurrence>()
        for (match in VIN_REGEX.findAll(norm)) {
            val span = SourceSpan(match.range.first, match.range.last + 1)
            if (!hasBoundaries(span.start, span.end)) continue
            val value = text.substring(span).uppercase()
            result += Occurrence(
                span,
                value,
                if (isValidVinChecksum(value)) ExtractionConfidence.HIGH else ExtractionConfidence.LOW,
            )
        }
        return result
    }

    private fun findPlateSpans(): List<SourceSpan> {
        val matches = mutableListOf<SourceSpan>()
        for (match in PLATE_REGEX.findAll(norm)) {
            val span = SourceSpan(match.range.first, match.range.last + 1)
            if (!hasBoundaries(span.start, span.end)) continue
            // Reject letter parts that are engine-tech tokens ("motor 2017 TDI" is not a plate).
            val letters = norm.substring(span).filter { it.isLetter() }
            if (PLATE_LETTER_STOPLIST.contains(letters.lowercase())) continue
            // Only the modern format (dddd LLL, four digits) is unambiguous by itself. The shorter
            // shapes also match ordinary prose ("2 VECES", "3 veces"), so they count only when a
            // plate label sits just before them.
            if (!hasModernPlateDigits(span) && !hasPlateLabelBefore(span.start)) continue
            matches += span
        }
        // Longest format wins when one match contains another.
        val deduped = dedupeByContainment(matches)
        // A ddddLLL candidate whose digits look like a model year ("Corolla 2019 con") is almost
        // never a plate when the note also contains a non-year-like plate; keep it only as a last
        // resort so single-plate notes still get their value.
        if (deduped.size > 1 && deduped.any { !looksLikeYearPlate(it) }) {
            return deduped.filterNot { looksLikeYearPlate(it) }
        }
        return deduped
    }

    private fun looksLikeYearPlate(span: SourceSpan): Boolean =
        YEAR_REGEX.matches(norm.substring(span).filter { it.isDigit() })

    /** The modern plate format carries exactly four digits ("1234 ABC"). */
    private fun hasModernPlateDigits(span: SourceSpan): Boolean =
        norm.substring(span).count { it.isDigit() } == 4

    /** True when a "matrícula" label sits within [PLATE_LABEL_WINDOW] chars before [index]. */
    private fun hasPlateLabelBefore(index: Int): Boolean {
        val from = (index - PLATE_LABEL_WINDOW).coerceAtLeast(0)
        return norm.substring(from, index).contains("matricul")
    }

    private fun findBrandMentions(): List<Anchor> {
        val mentions = mutableListOf<Anchor>()
        for (brand in brands) {
            val normBrand = normalizeText(brand)
            if (normBrand.isEmpty()) continue
            // allowPluralS: "mis dos toyotas" still points at the brand "toyota".
            for (span in findOccurrences(normBrand, allowPluralS = true)) {
                // Canonical catalog name as value; the span keeps the note's original spelling.
                mentions += Anchor(span, Anchor.Kind.BRAND, brand)
            }
        }
        return mentions.sortedBy { it.span.start }
    }

    // --- Field detection ----------------------------------------------------------------------

    private fun findOilSpecs(): List<Occurrence> {
        val result = mutableListOf<Occurrence>()
        for (match in OIL_SPEC_REGEX.findAll(norm)) {
            val span = SourceSpan(match.range.first, match.range.last + 1)
            if (!hasBoundaries(span.start, span.end)) continue
            val hasBrandNearby = OIL_BRANDS.any { brand ->
                findOccurrences(brand).any { it.centerDistanceTo(span) <= OIL_BRAND_WINDOW }
            }
            // SAE viscosity codes are written with an uppercase W ("5W30") and OCR often reads the
            // digit 0 as the letter O ("1OW30"); normalize both before returning the value.
            val value = text.substring(span).uppercase().replace('O', '0')
            result += Occurrence(span, value, if (hasBrandNearby) ExtractionConfidence.HIGH else ExtractionConfidence.MEDIUM)
        }
        return result
    }

    private fun findTireSizes(): List<SourceSpan> {
        val result = mutableListOf<SourceSpan>()
        for (match in TIRE_SIZE_REGEX.findAll(norm)) {
            val span = SourceSpan(match.range.first, match.range.last + 1)
            if (hasBoundaries(span.start, span.end)) result += span
        }
        return result
    }

    private fun findFuelTypes(): List<Occurrence> {
        val matches = mutableListOf<Pair<SourceSpan, String>>()
        for (keyword in FUEL_KEYWORDS.sortedByDescending { it.length }) {
            for (span in findOccurrences(keyword)) {
                if (matches.any { it.first.overlaps(span) }) continue
                matches += span to text.substring(span)
            }
        }
        return matches.map { (span, value) -> Occurrence(span, value) }
    }

    private fun findVehicleTypes(): List<Occurrence> {
        val matches = mutableListOf<Pair<SourceSpan, String>>() // span to catalog code
        for ((keyword, code) in VEHICLE_TYPE_KEYWORDS.sortedByDescending { it.first.length }) {
            for (span in findOccurrences(keyword)) {
                if (matches.any { it.first.overlaps(span) }) continue
                matches += span to code
            }
        }
        // European L-category (L1e..L7e) is the motorcycle type on ITV cards and data sheets.
        // The regex is a full token, so the bare "L" field marks (L.1, L.2, L.6) never match.
        for (match in MOTORCYCLE_CATEGORY_REGEX.findAll(norm)) {
            val span = SourceSpan(match.range.first, match.range.last + 1)
            if (!hasBoundaries(span.start, span.end)) continue
            if (matches.any { it.first.overlaps(span) }) continue
            matches += span to "motorcycle"
        }
        return matches.map { (span, code) -> Occurrence(span, code) }
    }

    /**
     * Conservative fallback for the commercial model on documents without a "Modelo:" label and
     * with the brand outside the catalog. Scans identifier tokens near the VIN and keeps the
     * longest one shaped like a model (letters + digits, e.g. "CBR500RA"), skipping the VIN
     * itself, homologation types with slashes ("PC62/1/1") and field codes (punctuated, so they
     * never match the model shape).
     *
     * Spare-part references are also model-shaped ("W67", "OC534"), so known parts brands, the
     * plate letter stoplist and any token introduced by a parts brand ("Mahle OC534") are skipped.
     */
    private fun findModelFallback(vinAnchor: Anchor?): ExtractedField<String>? {
        val vin = vinAnchor ?: return null
        var best: ExtractedField<String>? = null
        var i = 0
        while (i < text.length) {
            if (!text[i].isLetterOrDigit() && text[i] != '/') {
                i++
                continue
            }
            val start = i
            while (i < text.length && (text[i].isLetterOrDigit() || text[i] == '/')) i++
            val token = text.substring(start, i)
            val span = SourceSpan(start, i)
            if (token.contains('/')) continue
            if (span.overlaps(vin.span)) continue
            if (!MODEL_TOKEN_REGEX.matches(token)) continue
            val normalized = token.lowercase()
            if (normalized in KNOWN_PARTS_BRANDS || normalized in PLATE_LETTER_STOPLIST) continue
            if (precedingWord(start).lowercase() in KNOWN_PARTS_BRANDS) continue
            if (span.centerDistanceTo(vin.span) > MODEL_FALLBACK_DISTANCE) continue
            if (best == null || token.length > best.value.length) {
                best = ExtractedField(token, ExtractionConfidence.MEDIUM, span)
            }
        }
        return best
    }

    /** Letter run immediately before [index] (skipping separators), or an empty string. */
    private fun precedingWord(index: Int): String {
        var i = index - 1
        while (i >= 0 && !text[i].isLetter()) i--
        if (i < 0) return ""
        val end = i + 1
        while (i >= 0 && text[i].isLetter()) i--
        return text.substring(i + 1, end)
    }

    private fun findEngineDetails(): List<Occurrence> {
        val result = mutableListOf<Occurrence>()
        for (match in ENGINE_DETAIL_REGEX.findAll(norm)) {
            val span = SourceSpan(match.range.first, match.range.last + 1)
            if (!hasBoundaries(span.start, span.end)) continue
            val value = text.substring(span)
            // "1.6 TDI" is more specific than a bare displacement like "2.0".
            val confidence = if (value.any { it.isLetter() }) ExtractionConfidence.MEDIUM else ExtractionConfidence.LOW
            result += Occurrence(span, value, confidence)
        }
        return result
    }

    /**
     * Bulb codes written after a bulb/lights keyword ("Bombillas HI2R", "Bombillas delanteras:
     * H7", "Luces traseras H4"). Only the first code-shaped token on the same line is taken and
     * the side is read from the text between the keyword and the code, so prose lines without a
     * code ("se me fundieron las luces") are ignored.
     */
    private fun findBulbMentions(): List<BulbMention> {
        val result = mutableListOf<BulbMention>()
        for (keyword in BULB_KEYWORDS) {
            for (keywordSpan in findOccurrences(keyword)) {
                val lineEnd = lineEndAfter(keywordSpan.end)
                val segment = norm.substring(keywordSpan.end, lineEnd)
                for (match in BULB_CODE_REGEX.findAll(segment)) {
                    val codeStart = keywordSpan.end + match.range.first
                    val codeEnd = keywordSpan.end + match.range.last + 1
                    if (!hasBoundaries(codeStart, codeEnd)) continue
                    val codeSpan = SourceSpan(codeStart, codeEnd)
                    if (result.any { it.occurrence.span.overlaps(codeSpan) }) break
                    val sideText = norm.substring(keywordSpan.end, codeStart)
                    val side = when {
                        FRONT_SIDE_REGEX.containsMatchIn(sideText) -> BulbSide.FRONT
                        REAR_SIDE_REGEX.containsMatchIn(sideText) -> BulbSide.REAR
                        else -> null
                    }
                    result += BulbMention(
                        Occurrence(codeSpan, text.substring(codeSpan).uppercase(), ExtractionConfidence.MEDIUM),
                        side,
                    )
                    break
                }
            }
        }
        return result
    }

    /**
     * Battery specs following a "Batería" keyword ("Batería AGM 60Ah 540 arranque"). An "Ah"
     * capacity is required, so prose mentions without specs ("comprar batería") are ignored.
     */
    private fun findBatterySpecs(): List<Occurrence> {
        val result = mutableListOf<Occurrence>()
        for (keywordSpan in findOccurrences("bateria")) {
            val lineEnd = lineEndAfter(keywordSpan.end)
            val segment = norm.substring(keywordSpan.end, lineEnd)
            val match = BATTERY_SPEC_REGEX.find(segment) ?: continue
            var start = keywordSpan.end + match.range.first
            val end = keywordSpan.end + match.range.last + 1
            while (start < end && text[start].isWhitespace()) start++
            if (!hasBoundaries(start, end)) continue
            val span = SourceSpan(start, end)
            if (result.any { it.span.overlaps(span) }) continue
            result += Occurrence(span, normalizeOcrText(text.substring(span)), ExtractionConfidence.MEDIUM)
        }
        return result
    }

    /**
     * Coolant standards following a "Refrigerante" / "Anticongelante" keyword ("Refrigerante:
     * G12"). Only the standard G-spec code is taken, so prose without a code is ignored.
     */
    private fun findCoolants(): List<Occurrence> {
        val result = mutableListOf<Occurrence>()
        for (keyword in COOLANT_KEYWORDS) {
            for (keywordSpan in findOccurrences(keyword)) {
                val lineEnd = lineEndAfter(keywordSpan.end)
                val segment = norm.substring(keywordSpan.end, lineEnd)
                val match = COOLANT_SPEC_REGEX.find(segment) ?: continue
                val start = keywordSpan.end + match.range.first
                val end = keywordSpan.end + match.range.last + 1
                if (!hasBoundaries(start, end)) continue
                val span = SourceSpan(start, end)
                if (result.any { it.span.overlaps(span) }) continue
                result += Occurrence(span, text.substring(span).uppercase(), ExtractionConfidence.MEDIUM)
            }
        }
        return result
    }

    /** End offset of the line containing [index] (excluding the newline itself). */
    private fun lineEndAfter(index: Int): Int {
        val lineEnd = text.indexOf('\n', index)
        return if (lineEnd < 0) text.length else lineEnd
    }

    private fun findYears(): List<SourceSpan> {
        val result = mutableListOf<SourceSpan>()
        for (match in YEAR_REGEX.findAll(norm)) {
            val span = SourceSpan(match.range.first, match.range.last + 1)
            if (!hasBoundaries(span.start, span.end)) continue
            result += span
        }
        return result
    }

    // --- Spare parts ---------------------------------------------------------------------------

    private data class PartMatch(val span: SourceSpan, val category: String, val type: String?)

    private fun findSparePartMatches(): List<PartMatch> {
        val matches = mutableListOf<Pair<SourceSpan, PartKeyword>>()
        for (keyword in PART_KEYWORDS) { // already sorted longest-first
            for (span in findOccurrences(keyword.phrase)) {
                if (matches.any { it.first.overlaps(span) }) continue
                matches += span to keyword
            }
        }
        return matches.map { (span, keyword) -> PartMatch(span, keyword.category, keyword.type) }
    }

    private fun buildSparePart(
        match: PartMatch,
        vehicleIndex: Int?,
        nextPartSpan: SourceSpan?,
        quantity: QuantityMatch? = null,
    ): CandidateSparePart {
        val confidence = if (match.type != null) ExtractionConfidence.MEDIUM else ExtractionConfidence.LOW
        return CandidateSparePart(
            description = ExtractedField(value = text.substring(match.span), confidence = confidence, span = match.span),
            category = ExtractedField(match.category, confidence, match.span),
            type = match.type?.let { ExtractedField(it, confidence, match.span) },
            brand = findPartsBrandNear(match.span),
            reference = findReferenceNear(match.span, nextPartSpan),
            quantity = quantity?.quantity?.toDoubleField(),
            unit = quantity?.unit?.toField(),
            vehicleIndex = vehicleIndex,
        )
    }

    private fun findPartsBrandNear(span: SourceSpan): ExtractedField<String>? {
        var best: Pair<SourceSpan, Int>? = null // (word span, distance)
        for (word in wordsAround(span, beforeTokens = 2, afterTokens = 2)) {
            val wordText = norm.substring(word)
            if (!KNOWN_PARTS_BRANDS.contains(wordText)) continue
            // Require capitalization in the original text to avoid "en total" -> Total.
            if (!text[word.start].isUpperCase()) continue
            val distance = span.centerDistanceTo(word)
            if (best == null || distance < best.second) best = word to distance
        }
        return best?.let { (word, _) -> ExtractedField(value = text.substring(word), confidence = ExtractionConfidence.MEDIUM, span = word) }
    }

    /**
     * Looks for a "ref." marker after the part phrase (references follow the part in natural
     * word order: "filtro de aceite Mann ref. W67/2"). The search stops at [stopAt] — the start
     * of the next part phrase — so densely listed parts do not steal each other's references.
     */
    private fun findReferenceNear(span: SourceSpan, stopAt: SourceSpan?): ExtractedField<String>? {
        val regionEnd = minOf(
            (span.end + REFERENCE_WINDOW).coerceAtMost(norm.length),
            stopAt?.start ?: norm.length,
        )
        for (match in REFERENCE_REGEX.findAll(norm, span.end)) {
            if (match.range.first > regionEnd) break
            val tokenRange = match.trailingGroupRange(1)
            val tokenSpan = SourceSpan(tokenRange.first, tokenRange.last + 1)
            return ExtractedField(value = text.substring(tokenSpan), confidence = ExtractionConfidence.HIGH, span = tokenSpan)
        }
        return null
    }

    // --- Maintenances ---------------------------------------------------------------------------

    private data class ActionMatch(val span: SourceSpan, val specific: Boolean)

    private fun findMaintenanceActions(): List<ActionMatch> {
        val matches = mutableListOf<Pair<SourceSpan, ActionKeyword>>()
        for (keyword in MAINTENANCE_ACTIONS) { // already sorted longest-first
            for (span in findOccurrences(keyword.phrase)) {
                if (matches.any { it.first.overlaps(span) }) continue
                matches += span to keyword
            }
        }
        return matches.map { (span, keyword) -> ActionMatch(span, keyword.specific) }
    }

    private fun hasMaintainableField(action: ActionMatch): Boolean {
        val region = action.span.grow(MAINTENANCE_FIELD_WINDOW)
        // A bare model year nearby is not enough to keep an action
        // ("Corolla 2019. Hice un cambio de aceite." has no maintenance context).
        return findDateInRegion(region, action.span, bareYearAllowed = false) != null ||
            findKilometersInRegion(region, action.span) != null ||
            findCostInRegion(region, action.span) != null
    }

    private fun buildMaintenance(action: ActionMatch, vehicleIndex: Int?): CandidateMaintenance {
        val region = action.span.grow(MAINTENANCE_FIELD_WINDOW)
        return CandidateMaintenance(
            description = ExtractedField(
                value = text.substring(action.span),
                confidence = if (action.specific) ExtractionConfidence.MEDIUM else ExtractionConfidence.LOW,
                span = action.span,
            ),
            date = findDateInRegion(region, action.span),
            kilometers = findKilometersInRegion(region, action.span),
            cost = findCostInRegion(region, action.span),
            vehicleIndex = vehicleIndex,
        )
    }

    private fun findDateInRegion(
        region: SourceSpan,
        anchor: SourceSpan,
        bareYearAllowed: Boolean = true,
    ): ExtractedField<String>? {
        // Priority: explicit numeric dates > month names (with optional year) > bare year.
        val candidates = mutableListOf<Pair<SourceSpan, ExtractionConfidence>>()
        for (regex in listOf(DATE_SLASH_REGEX, DATE_ISO_REGEX)) {
            for (match in regex.findAll(norm)) {
                val span = SourceSpan(match.range.first, match.range.last + 1)
                // A date already used as a vehicle field ("Fecha de matriculación", "ITV") is not
                // maintenance evidence.
                if (!hasBoundaries(span.start, span.end) || usedSpans.contains(span) || !region.containsOrTouches(span)) continue
                candidates += span to ExtractionConfidence.MEDIUM
            }
        }
        if (candidates.isEmpty()) {
            for (match in DATE_MONTH_REGEX.findAll(norm)) {
                val span = SourceSpan(match.range.first, match.range.last + 1)
                if (!hasBoundaries(span.start, span.end) || usedSpans.contains(span) || !region.containsOrTouches(span)) continue
                candidates += span to ExtractionConfidence.LOW
            }
        }
        if (candidates.isEmpty() && bareYearAllowed) {
            for (span in findYears()) {
                if (region.containsOrTouches(span)) candidates += span to ExtractionConfidence.LOW
            }
        }
        val best = candidates.minByOrNull { it.first.centerDistanceTo(anchor) } ?: return null
        return ExtractedField(value = text.substring(best.first), confidence = best.second, span = best.first)
    }

    private fun findKilometersInRegion(region: SourceSpan, anchor: SourceSpan): ExtractedField<Int>? {
        // Numeric form first ("40.000 km"), then number words ("cuarenta mil kilómetros").
        for (match in KM_NUMERIC_REGEX.findAll(norm)) {
            val span = SourceSpan(match.range.first, match.range.last + 1)
            if (!hasBoundaries(span.start, span.end) || !region.containsOrTouches(span)) continue
            val numberRange = match.leadingGroupRange(1)
            val value = norm.substring(numberRange).replace(".", "").replace(",", "").toIntOrNull() ?: continue
            return ExtractedField(value = value, confidence = ExtractionConfidence.MEDIUM, span = SourceSpan(numberRange.first, numberRange.last + 1))
        }
        var best: Triple<SourceSpan, Int, Int>? = null // (span, value, distance)
        for (unit in KM_UNIT_REGEX.findAll(norm)) {
            val unitSpan = SourceSpan(unit.range.first, unit.range.last + 1)
            if (!hasBoundaries(unitSpan.start, unitSpan.end) || !region.containsOrTouches(unitSpan)) continue
            // Longest trailing suffix of the preceding words that parses as a number.
            val wordSpans = wordsBefore(unitSpan.start, maxTokens = 6)
            var parsed: Pair<Int, Int>? = null // (token index, value)
            for (i in wordSpans.indices) {
                val tokens = wordSpans.subList(i, wordSpans.size).map { norm.substring(it) }
                val value = parseSpanishNumberWords(tokens) ?: continue
                parsed = i to value
                break
            }
            if (parsed == null) continue
            val spanStart = wordSpans[parsed.first].start
            val distance = unitSpan.centerDistanceTo(anchor)
            if (best == null || distance < best.third) {
                best = Triple(SourceSpan(spanStart, unitSpan.end), parsed.second, distance)
            }
        }
        return best?.let { (span, value, _) -> ExtractedField(value = value, confidence = ExtractionConfidence.LOW, span = span) }
    }

    private fun findCostInRegion(region: SourceSpan, anchor: SourceSpan): ExtractedField<Double>? {
        var best: Pair<SourceSpan, Double>? = null
        for (match in COST_REGEX.findAll(norm)) {
            val numberRange = match.leadingGroupRange(1)
            if (!region.containsOrTouches(SourceSpan(numberRange.first, numberRange.last + 1))) continue
            val value = parseDecimal(norm.substring(numberRange)) ?: continue
            val span = SourceSpan(numberRange.first, numberRange.last + 1)
            if (best == null || span.centerDistanceTo(anchor) < best.first.centerDistanceTo(anchor)) {
                best = span to value
            }
        }
        return best?.let { (span, value) -> ExtractedField(value = value, confidence = ExtractionConfidence.MEDIUM, span = span) }
    }

    // --- Association -----------------------------------------------------------------------------

    private fun nearestVehicleIndex(assignments: List<Pair<SourceSpan, Int>>, span: SourceSpan): Int? {
        var best: Pair<Int, Int>? = null // (vehicleIndex, distance)
        for ((anchorSpan, vehicleIndex) in assignments) {
            val distance = anchorSpan.centerDistanceTo(span)
            if (distance <= ASSOCIATION_WINDOW && (best == null || distance < best.second)) {
                best = vehicleIndex to distance
            }
        }
        return best?.first
    }

    // --- Text helpers ------------------------------------------------------------------------------

    private fun hasBoundaries(start: Int, end: Int): Boolean {
        val beforeOk = start == 0 || !isWordChar(norm[start - 1])
        val afterOk = end >= norm.length || !isWordChar(norm[end])
        return beforeOk && afterOk
    }

    private fun isWordChar(c: Char): Boolean = c.isLetterOrDigit()

    /** All occurrences of [phrase] in the normalized text with word boundaries. */
    private fun findOccurrences(phrase: String, allowPluralS: Boolean = false): List<SourceSpan> {
        if (phrase.isEmpty()) return emptyList()
        val spans = mutableListOf<SourceSpan>()
        var index = norm.indexOf(phrase)
        while (index >= 0) {
            val end = index + phrase.length
            val beforeOk = index == 0 || !isWordChar(norm[index - 1])
            val afterOk = end >= norm.length || !isWordChar(norm[end]) || (allowPluralS && norm[end] == 's')
            if (beforeOk && afterOk) spans += SourceSpan(index, end)
            index = norm.indexOf(phrase, index + 1)
        }
        return spans
    }

    /** Drops spans contained in another span (longest match wins). */
    private fun dedupeByContainment(spans: List<SourceSpan>): List<SourceSpan> {
        val sorted = spans.sortedWith(compareByDescending<SourceSpan> { it.end - it.start }.thenBy { it.start })
        val result = mutableListOf<SourceSpan>()
        for (span in sorted) {
            if (result.none { it.overlaps(span) }) result += span
        }
        return result.sortedBy { it.start }
    }

    /** Up to [maxTokens] letter runs immediately before [index], farthest first. */
    private fun wordsBefore(index: Int, maxTokens: Int): List<SourceSpan> {
        val result = mutableListOf<SourceSpan>()
        var i = index - 1
        while (i >= 0 && result.size < maxTokens) {
            while (i >= 0 && !norm[i].isLetter()) i--
            if (i < 0) break
            val end = i + 1
            while (i >= 0 && norm[i].isLetter()) i--
            result.add(0, SourceSpan(i + 1, end))
        }
        return result
    }

    /** Letter runs within [beforeTokens]/[afterTokens] words around a span. */
    private fun wordsAround(span: SourceSpan, beforeTokens: Int, afterTokens: Int): List<SourceSpan> {
        val before = wordsBefore(span.start, beforeTokens)
        val after = mutableListOf<SourceSpan>()
        var i = span.end
        while (i < norm.length && after.size < afterTokens) {
            while (i < norm.length && !norm[i].isLetter()) i++
            if (i >= norm.length) break
            val start = i
            while (i < norm.length && norm[i].isLetter()) i++
            after += SourceSpan(start, i)
        }
        return before + after
    }

    private fun SourceSpan.grow(chars: Int): SourceSpan =
        SourceSpan((start - chars).coerceAtLeast(0), (end + chars).coerceAtMost(norm.length))

    private fun SourceSpan.containsOrTouches(other: SourceSpan): Boolean = other.start <= end && start <= other.end

    // --- Label / value parsing (data sheets and invoices) -------------------------------------

    /**
     * `Etiqueta: valor` pairs, computed once. Label handling is additive: free-text detection
     * still runs, and a clean regex match is preferred when both find the same span.
     */
    private val labelledFields: LabelledFields by lazy { collectLabelledFields() }

    private class LabelledFields(
        val vinAnchors: List<Anchor>,
        val plateAnchors: List<Anchor>,
        val brandAnchors: List<Anchor>,
        val models: List<Occurrence>,
        val oilSpecs: List<Occurrence>,
        val tireSizes: List<Occurrence>,
        val fuelTypes: List<Occurrence>,
        val engineDetails: List<Occurrence>,
        val cylinderCounts: List<Occurrence>,
        val firstRegistrations: List<Occurrence>,
        val nextItvs: List<Occurrence>,
        val frontPressuresBelow160: List<Occurrence>,
        val rearPressuresBelow160: List<Occurrence>,
        val frontPressuresAbove160: List<Occurrence>,
        val rearPressuresAbove160: List<Occurrence>,
        val invoiceDates: List<ExtractedField<String>>,
        val invoiceKilometers: List<ExtractedField<Int>>,
        val invoiceTotals: List<LabelledValue>,
        val workshops: List<Occurrence>,
        val mechanics: List<Occurrence>,
    )

    private fun collectLabelledFields(): LabelledFields {
        val values = findLabelledValues()
        fun of(field: LabelledField) = values.filter { it.field == field }

        val vinAnchors = of(LabelledField.VIN).mapNotNull { value ->
            val cleaned = cleanIdentifier(value.rawValue)
            if (cleaned.length != 17) return@mapNotNull null
            val cleanOk = isValidVinChecksum(cleaned)
            val repaired = if (cleanOk) cleaned else repairVin(cleaned)
            Anchor(
                span = value.span,
                kind = Anchor.Kind.VIN,
                value = repaired ?: cleaned,
                confidence = when {
                    cleanOk -> ExtractionConfidence.HIGH
                    repaired != null -> ExtractionConfidence.MEDIUM
                    else -> ExtractionConfidence.LOW
                },
            )
        }
        val plateAnchors = of(LabelledField.PLATE).mapNotNull { value ->
            val cleaned = cleanIdentifier(value.rawValue)
            if (cleaned.isEmpty()) return@mapNotNull null
            val cleanOk = PLATE_REGEX.matches(cleaned.lowercase())
            val repaired = if (cleanOk) cleaned else repairPlate(cleaned)
            Anchor(
                span = value.span,
                kind = Anchor.Kind.PLATE,
                value = repaired,
                confidence = when {
                    cleanOk -> ExtractionConfidence.HIGH
                    PLATE_REGEX.matches(repaired.lowercase()) -> ExtractionConfidence.MEDIUM
                    else -> ExtractionConfidence.LOW
                },
            )
        }
        val brandAnchors = of(LabelledField.BRAND).map {
            // Raw value: dedupe keeps the canonical catalog spelling when the brand is known.
            Anchor(it.span, Anchor.Kind.BRAND, it.rawValue, ExtractionConfidence.HIGH)
        }
        val frontPressures = of(LabelledField.TIRE_PRESSURE_FRONT)
        val rearPressures = of(LabelledField.TIRE_PRESSURE_REAR)
        return LabelledFields(
            vinAnchors = vinAnchors,
            plateAnchors = plateAnchors,
            brandAnchors = brandAnchors,
            models = of(LabelledField.MODEL).map { Occurrence(it.span, it.rawValue, ExtractionConfidence.HIGH) },
            oilSpecs = of(LabelledField.OIL).map { Occurrence(it.span, it.rawValue, ExtractionConfidence.MEDIUM) },
            tireSizes = of(LabelledField.TIRE_SIZE).mapNotNull { tireSizeFromLabel(it) },
            fuelTypes = of(LabelledField.FUEL).map { Occurrence(it.span, it.rawValue, ExtractionConfidence.MEDIUM) },
            engineDetails = of(LabelledField.DISPLACEMENT).map { Occurrence(it.span, it.rawValue, ExtractionConfidence.MEDIUM) },
            cylinderCounts = of(LabelledField.CYLINDERS).mapNotNull { value ->
                val (span, raw) = numericSpan(value, LABELLED_AMOUNT_REGEX) ?: return@mapNotNull null
                Occurrence(span, raw, ExtractionConfidence.HIGH)
            },
            firstRegistrations = of(LabelledField.FIRST_REGISTRATION).map { Occurrence(it.span, it.rawValue, ExtractionConfidence.MEDIUM) },
            nextItvs = of(LabelledField.NEXT_ITV).map { Occurrence(it.span, it.rawValue, ExtractionConfidence.MEDIUM) },
            frontPressuresBelow160 = frontPressures.filter { it.speedQualifier != 1 }.mapNotNull { pressureOccurrence(it) },
            rearPressuresBelow160 = rearPressures.filter { it.speedQualifier != 1 }.mapNotNull { pressureOccurrence(it) },
            frontPressuresAbove160 = frontPressures.filter { it.speedQualifier == 1 }.mapNotNull { pressureOccurrence(it) },
            rearPressuresAbove160 = rearPressures.filter { it.speedQualifier == 1 }.mapNotNull { pressureOccurrence(it) },
            invoiceDates = of(LabelledField.DATE).map { ExtractedField(it.rawValue, ExtractionConfidence.MEDIUM, it.span) },
            invoiceKilometers = of(LabelledField.KILOMETERS).mapNotNull { value ->
                val (span, raw) = numericSpan(value, LABELLED_AMOUNT_REGEX) ?: return@mapNotNull null
                val digits = raw.filter { it.isDigit() }.toIntOrNull() ?: return@mapNotNull null
                ExtractedField(digits, ExtractionConfidence.MEDIUM, span)
            },
            invoiceTotals = of(LabelledField.TOTAL),
            workshops = of(LabelledField.WORKSHOP).map { Occurrence(it.span, it.rawValue, ExtractionConfidence.MEDIUM) },
            mechanics = of(LabelledField.MECHANIC).map { Occurrence(it.span, it.rawValue, ExtractionConfidence.MEDIUM) },
        )
    }

    /** Splits the note into lines and parses the `Etiqueta: valor` pairs it recognizes. */
    private fun findLabelledValues(): List<LabelledValue> {
        val result = mutableListOf<LabelledValue>()
        var lineStart = 0
        while (lineStart <= text.length) {
            var lineEnd = text.indexOf('\n', lineStart)
            if (lineEnd < 0) lineEnd = text.length
            val contentEnd = if (lineEnd > lineStart && text[lineEnd - 1] == '\r') lineEnd - 1 else lineEnd
            parseLabelledLine(lineStart, contentEnd)?.let { result += it }
            if (lineEnd >= text.length) break
            lineStart = lineEnd + 1
        }
        return result
    }

    private fun parseLabelledLine(start: Int, end: Int): LabelledValue? {
        if (end <= start) return null
        val separator = findSeparator(start, end) ?: return null
        val labelEnd = separator.first
        val valueStart = separator.second
        val explicit = separator.third
        val rawLabel = normalizeOcrText(text.substring(start, labelEnd))
        val label = cleanLabel(rawLabel)
        if (label.isEmpty() || label.length > 40) return null
        // A long concept line followed by a quantity ("FILTRO ACEITE MANN REF. W67/2  2 ud") is not
        // a label: when only 2+ spaces separate, keep it to a handful of words.
        if (!explicit && label.count { it == ' ' } > 2) return null
        val field = matchLabelledField(label) ?: return null
        var valueEnd = end
        while (valueEnd > valueStart && text[valueEnd - 1].isWhitespace()) valueEnd--
        if (valueEnd <= valueStart) return null
        val rawValue = normalizeOcrText(text.substring(valueStart, valueEnd))
        if (rawValue.isEmpty()) return null
        // "Aceite de motor Repsol  4 L" is a part line, not the vehicle's oil spec.
        if (field == LabelledField.OIL && !OIL_SPEC_REGEX.containsMatchIn(rawValue) &&
            label.count { it == ' ' } > 1
        ) return null
        return LabelledValue(
            field = field,
            label = label,
            rawValue = rawValue,
            span = SourceSpan(valueStart, valueEnd),
            speedQualifier = speedQualifierFor(rawLabel, rawValue),
        )
    }

    /** Earliest `:`, tab or 2+ space run in the line, as (labelEnd, firstValueChar, explicit). */
    private fun findSeparator(start: Int, end: Int): Triple<Int, Int, Boolean>? {
        var labelEnd = -1
        var valueStart = -1
        var explicit = false
        fun consider(candidateLabelEnd: Int, candidateValueStart: Int, isExplicit: Boolean) {
            if (labelEnd < 0 || candidateLabelEnd < labelEnd) {
                labelEnd = candidateLabelEnd
                valueStart = candidateValueStart
                explicit = isExplicit
            }
        }
        // Bound both searches to this line: an unbounded indexOf would rescan the whole note
        // for every line (O(n^2) on a long note) only to discard the out-of-line result.
        val line = text.substring(start, end)
        val colon = line.indexOf(':')
        if (colon >= 0) consider(start + colon, start + colon + 1, true)
        val tab = line.indexOf('\t')
        if (tab >= 0) consider(start + tab, start + tab + 1, true)
        var i = start
        while (i < end - 1) {
            if (text[i] == ' ' && text[i + 1] == ' ') {
                var j = i
                while (j < end && text[j] == ' ') j++
                consider(i, j, false)
                break
            }
            i++
        }
        if (labelEnd < 0) return null
        var vs = valueStart
        while (vs < end && text[vs].isWhitespace()) vs++
        if (vs >= end) return null
        return Triple(labelEnd, vs, explicit)
    }

    /** Lowercases/accent-strips the label and keeps only letters, digits and single spaces. */
    private fun cleanLabel(raw: String): String =
        normalizeText(raw).replace(Regex("[^a-z0-9 ]"), " ").replace(Regex("\\s+"), " ").trim()

    /** "-1" for a "<160" pressure label, "1" for ">160", null when it does not say. */
    private fun speedQualifierFor(label: String, value: String): Int? {
        val haystack = (label + " " + value).lowercase()
        return when {
            ">160" in haystack || "mas de 160" in haystack || "superior a 160" in haystack ||
                "alta velocidad" in haystack -> 1
            "<160" in haystack || "menos de 160" in haystack || "hasta 160" in haystack ||
                "inferior a 160" in haystack -> -1
            else -> null
        }
    }

    private fun pressureOccurrence(value: LabelledValue): Occurrence? {
        val (span, raw) = numericSpan(value, LABELLED_AMOUNT_REGEX) ?: return null
        val number = parseDecimal(raw) ?: return null
        return Occurrence(span, number.toString(), ExtractionConfidence.MEDIUM)
    }

    private fun tireSizeFromLabel(value: LabelledValue): Occurrence? {
        val match = TIRE_SIZE_REGEX.find(norm, value.span.start)
        if (match != null && match.range.first < value.span.end) {
            val span = SourceSpan(match.range.first, match.range.last + 1)
            return Occurrence(span, text.substring(span).uppercase(), ExtractionConfidence.MEDIUM)
        }
        return Occurrence(value.span, value.rawValue, ExtractionConfidence.MEDIUM)
    }

    /** First number inside a label value, with its exact span in the original text. */
    private fun numericSpan(value: LabelledValue, regex: Regex): Pair<SourceSpan, String>? {
        val match = regex.find(value.rawValue) ?: return null
        return SourceSpan(value.span.start + match.range.first, value.span.start + match.range.last + 1) to match.value
    }

    private fun repairPlate(raw: String): String {
        val cleaned = cleanIdentifier(raw)
        if (PLATE_REGEX.matches(cleaned.lowercase())) return cleaned
        // Spanish formats: modern 4 digits + 3 letters, legacy 3+3, 2+4 or 1+5.
        val digitCounts = when (cleaned.length) {
            7 -> listOf(4)
            6 -> listOf(3, 2, 1)
            else -> listOf(cleaned.indexOfFirst { it.isLetter() }).filter { it > 0 }
        }
        for (digitCount in digitCounts) {
            if (digitCount !in 1 until cleaned.length) continue
            val numeric = cleaned.substring(0, digitCount).map { OCR_LETTER_TO_DIGIT[it] ?: it }.joinToString("")
            val letters = cleaned.substring(digitCount).map { OCR_DIGIT_TO_LETTER[it] ?: it }.joinToString("")
            if (numeric.all { it.isDigit() } && letters.all { it.isLetter() }) {
                val candidate = numeric + letters
                if (PLATE_REGEX.matches(candidate.lowercase())) return candidate
            }
        }
        return cleaned
    }

    // --- Invoice parsing ----------------------------------------------------------------------

    /** A workshop document needs an explicit marker, so casual "total" chatter is not an invoice. */
    private fun looksLikeInvoice(): Boolean {
        if (INVOICE_STRONG_MARKERS.any { findOccurrences(it).isNotEmpty() }) return true
        return findOccurrences("iva").isNotEmpty() && labelledFields.invoiceTotals.isNotEmpty()
    }

    private fun buildInvoiceMaintenance(
        assignments: List<Pair<SourceSpan, Int>>,
        clusterCount: Int,
        partMatches: List<PartMatch>,
    ): CandidateMaintenance? {
        val date = findInvoiceDate()
        val kilometers = findInvoiceKilometers()
        val cost = findInvoiceTotal()
        if (date == null && kilometers == null && cost == null && partMatches.isEmpty()) return null
        val description = findInvoiceDescription(partMatches)
        val vehicleIndex = if (clusterCount == 1) 0
        else nearestVehicleIndex(assignments, cost?.span ?: date?.span ?: description.span)
        return CandidateMaintenance(
            description = description,
            date = date,
            kilometers = kilometers,
            cost = cost,
            vehicleIndex = vehicleIndex,
            workshop = labelledFields.workshops.minByOrNull { it.span.start }?.toField()
                ?: findLineContaining("taller")?.let { ExtractedField(text.substring(it), ExtractionConfidence.MEDIUM, it) },
            mechanic = labelledFields.mechanics.minByOrNull { it.span.start }?.toField()
                ?: findLineContaining("mecanic")?.let { ExtractedField(text.substring(it), ExtractionConfidence.MEDIUM, it) },
        )
    }

    private fun findInvoiceDate(): ExtractedField<String>? {
        labelledFields.invoiceDates.minByOrNull { it.span.start }?.let { return it }
        for (regex in listOf(DATE_SLASH_REGEX, DATE_ISO_REGEX)) {
            val match = regex.find(norm) ?: continue
            val span = SourceSpan(match.range.first, match.range.last + 1)
            return ExtractedField(text.substring(span), ExtractionConfidence.MEDIUM, span)
        }
        DATE_MONTH_REGEX.find(norm)?.let { match ->
            val span = SourceSpan(match.range.first, match.range.last + 1)
            return ExtractedField(text.substring(span), ExtractionConfidence.LOW, span)
        }
        return null
    }

    private fun findInvoiceKilometers(): ExtractedField<Int>? {
        labelledFields.invoiceKilometers.minByOrNull { it.span.start }?.let { return it }
        for (match in KM_NUMERIC_REGEX.findAll(norm)) {
            val numberRange = match.leadingGroupRange(1)
            val value = norm.substring(numberRange).replace(".", "").replace(",", "").toIntOrNull() ?: continue
            return ExtractedField(value, ExtractionConfidence.MEDIUM, SourceSpan(numberRange.first, numberRange.last + 1))
        }
        return null
    }

    private fun findInvoiceTotal(): ExtractedField<Double>? {
        // "Total a pagar" wins over a bare "Total"; the tax base / VAT are never used.
        val best = labelledFields.invoiceTotals.maxWithOrNull(
            compareBy({ totalPriority(it.label) }, { it.span.start }),
        )
        if (best != null) {
            numericSpan(best, LABELLED_AMOUNT_REGEX)?.let { (span, raw) ->
                parseDecimal(raw)?.let { return ExtractedField(it, ExtractionConfidence.MEDIUM, span) }
            }
        }
        // Fallback: the last euro amount in the document (totals are printed last).
        var fallback: ExtractedField<Double>? = null
        for (match in COST_REGEX.findAll(norm)) {
            val numberRange = match.leadingGroupRange(1)
            val value = parseDecimal(norm.substring(numberRange)) ?: continue
            fallback = ExtractedField(value, ExtractionConfidence.LOW, SourceSpan(numberRange.first, numberRange.last + 1))
        }
        return fallback
    }

    private fun totalPriority(label: String): Int = when {
        "a pagar" in label -> 3
        "factura" in label -> 2
        else -> 1
    }

    private fun findInvoiceDescription(partMatches: List<PartMatch>): ExtractedField<String> {
        val action = findMaintenanceActions().sortedBy { it.span.start }.firstOrNull()
        if (action != null) {
            return ExtractedField(
                text.substring(action.span),
                if (action.specific) ExtractionConfidence.MEDIUM else ExtractionConfidence.LOW,
                action.span,
            )
        }
        partMatches.firstOrNull()?.let {
            return ExtractedField(text.substring(it.span), ExtractionConfidence.LOW, it.span)
        }
        val title = findOccurrences("factura").firstOrNull() ?: findOccurrences("orden de reparacion").firstOrNull()
        if (title != null) return ExtractedField(text.substring(title), ExtractionConfidence.LOW, title)
        val end = minOf(40, text.length)
        return ExtractedField(text.substring(0, end), ExtractionConfidence.LOW, SourceSpan(0, end))
    }

    /** Full trimmed line containing [phrase] in its normalized form (invoice header fallback). */
    private fun findLineContaining(phrase: String): SourceSpan? {
        var lineStart = 0
        while (lineStart <= text.length) {
            var lineEnd = text.indexOf('\n', lineStart)
            if (lineEnd < 0) lineEnd = text.length
            val contentEnd = if (lineEnd > lineStart && text[lineEnd - 1] == '\r') lineEnd - 1 else lineEnd
            if (norm.substring(lineStart, contentEnd).contains(phrase)) {
                var s = lineStart
                while (s < contentEnd && text[s].isWhitespace()) s++
                var e = contentEnd
                while (e > s && text[e - 1].isWhitespace()) e--
                if (e > s) return SourceSpan(s, e)
            }
            if (lineEnd >= text.length) break
            lineStart = lineEnd + 1
        }
        return null
    }

    // --- Maintenance log (cuaderno de mantenimiento) -----------------------------------------

    /** A row of a maintenance log: one (km, date) pair under a section header. */
    private data class LogRow(
        val description: String,
        val descriptionSpan: SourceSpan,
        val kilometers: ExtractedField<Int>,
        val date: ExtractedField<String>?,
        /** Section header this row belongs to; part of the merge key. */
        val header: String?,
    )

    /** Every non-empty line, trimmed, as an absolute span (keeps offsets valid). */
    private fun lineSpans(): List<SourceSpan> {
        val result = mutableListOf<SourceSpan>()
        var lineStart = 0
        while (lineStart <= text.length) {
            var lineEnd = text.indexOf('\n', lineStart)
            if (lineEnd < 0) lineEnd = text.length
            var start = lineStart
            var end = lineEnd
            if (end > start && text[end - 1] == '\r') end--
            while (start < end && text[start].isWhitespace()) start++
            while (end > start && text[end - 1].isWhitespace()) end--
            if (end > start) result += SourceSpan(start, end)
            if (lineEnd >= text.length) break
            lineStart = lineEnd + 1
        }
        return result
    }

    /** A maintenance log repeats short km rows; prose mentions kms inside long sentences. */
    private fun looksLikeLog(): Boolean {
        var rows = 0
        for (line in lineSpans()) {
            if (line.end - line.start > LOG_ROW_MAX_LENGTH) continue
            if (PROXIMA_ITV_REGEX.containsMatchIn(norm.substring(line))) continue
            if (rowKmMatch(line) != null) rows++
            if (rows >= 2) return true
        }
        return false
    }

    /**
     * Km match of a line, constrained to that line (the unit regex uses `\s`). A prose sentence
     * ("Cambié el aceite a los 40.000 km") also holds a km value, so the number must be preceded
     * only by a bullet or a short "label:" prefix to count as a log row.
     */
    private fun rowKmMatch(line: SourceSpan): MatchResult? =
        KM_NUMERIC_REGEX.find(norm, line.start)
            ?.takeIf {
                it.range.first >= line.start && it.range.last < line.end &&
                    isLogRowPrefix(line, it.range.first)
            }

    /** True when everything before the km number is a bullet or a short label ending in ":". */
    private fun isLogRowPrefix(line: SourceSpan, kmStart: Int): Boolean {
        val prefix = text.substring(line.start, kmStart).trim().trimStart('-', '*', '•').trim()
        return prefix.isEmpty() || prefix.endsWith(":")
    }

    private fun isHeaderLine(line: SourceSpan): Boolean {
        if (rowKmMatch(line) != null) return false
        val content = norm.substring(line)
        if (PROXIMA_ITV_REGEX.containsMatchIn(content)) return false
        if (DATE_SLASH_REGEX.containsMatchIn(content) || DATE_ISO_REGEX.containsMatchIn(content)) return false
        val trimmed = text.substring(line)
        if (trimmed.startsWith("-") || trimmed.startsWith("*") || trimmed.startsWith("•")) return false
        // A parenthesised continuation line ("ILSAC GF-6A)") is not a section header.
        if (trimmed.endsWith(")")) return false
        return trimmed.endsWith(":") || trimmed.length < 40
    }

    private fun findNextItvMentions(): List<Occurrence> {
        val result = mutableListOf<Occurrence>()
        for (match in PROXIMA_ITV_REGEX.findAll(norm)) {
            val range = match.trailingGroupRange(1)
            val span = SourceSpan(range.first, range.last + 1)
            if (!hasBoundaries(span.start, span.end)) continue
            result += Occurrence(span, text.substring(span), ExtractionConfidence.MEDIUM)
        }
        return result
    }

    /** First date on the row's line, else the date on the immediately following continuation line. */
    private fun findRowDate(line: SourceSpan, next: SourceSpan?): ExtractedField<String>? {
        findDateSpanIn(line)?.let { return it }
        if (next != null && rowKmMatch(next) == null &&
            !PROXIMA_ITV_REGEX.containsMatchIn(norm.substring(next))
        ) {
            findDateSpanIn(next)?.let { return it }
        }
        return null
    }

    private fun findDateSpanIn(line: SourceSpan): ExtractedField<String>? {
        var best: SourceSpan? = null
        for (regex in listOf(DATE_SLASH_REGEX, DATE_ISO_REGEX)) {
            for (match in regex.findAll(norm, line.start)) {
                if (match.range.last >= line.end) break
                val span = SourceSpan(match.range.first, match.range.last + 1)
                if (best == null || span.start < best.start) best = span
            }
        }
        return best?.let { ExtractedField(text.substring(it), ExtractionConfidence.MEDIUM, it) }
    }

    /** Relevant text after the km number: dates, parenthesised notes and "OK" markers removed. */
    private fun rowExtra(line: SourceSpan, km: MatchResult): String {
        val tail = text.substring(km.range.last + 1, line.end)
        val withoutParens = tail.replace(Regex("\\([^)]*\\)?"), " ")
        val withoutDates = DATE_SLASH_REGEX.replace(DATE_ISO_REGEX.replace(withoutParens, " "), " ")
        val withoutStatus = withoutDates.replace(Regex("(?i)\\b(ok|okay)\\b"), " ")
        return withoutStatus.trim().trim('-', ':', ',', '.', ';', '(', ')').trim()
    }

    private fun extractLogRows(): List<LogRow> {
        val lines = lineSpans()
        val rows = mutableListOf<LogRow>()
        var lastHeader: String? = null
        var lastHeaderSpan: SourceSpan? = null
        for ((index, line) in lines.withIndex()) {
            val km = rowKmMatch(line)
            if (km == null || PROXIMA_ITV_REGEX.containsMatchIn(norm.substring(line))) {
                if (isHeaderLine(line)) {
                    lastHeader = text.substring(line)
                    lastHeaderSpan = line
                }
                continue
            }
            val numberRange = km.leadingGroupRange(1)
            val kmValue = norm.substring(numberRange).replace(".", "").replace(",", "").toIntOrNull()
            if (kmValue == null) continue
            val kmSpan = SourceSpan(numberRange.first, numberRange.last + 1)
            val colon = norm.indexOf(':', line.start)
            val inlineHeader =
                if (colon in line.start until km.range.first) text.substring(line.start, colon).trim() else null
            val header = inlineHeader ?: lastHeader
            val headerSpan = if (inlineHeader != null) SourceSpan(line.start, colon) else lastHeaderSpan
            val date = findRowDate(line, lines.getOrNull(index + 1))
            val extra = rowExtra(line, km)
            val description = when {
                header.isNullOrEmpty() -> "Mantenimiento"
                extra.isEmpty() -> header
                else -> "$header $extra"
            }
            rows += LogRow(
                description = description,
                descriptionSpan = headerSpan ?: kmSpan,
                kilometers = ExtractedField(kmValue, ExtractionConfidence.MEDIUM, kmSpan),
                date = date,
                header = header,
            )
        }
        return rows
    }

    /**
     * One maintenance per log row. Rows sharing the same (km, date) are merged only when their
     * header combination repeats across several rows (two sections listing the same service
     * history, e.g. "Aceite" + "Filtro"): a single coincident date is kept as two maintenances.
     */
    private fun buildLogMaintenances(
        assignments: List<Pair<SourceSpan, Int>>,
        clusterCount: Int,
    ): List<CandidateMaintenance> {
        val rows = extractLogRows()
        if (rows.isEmpty()) return emptyList()
        val grouped = LinkedHashMap<Pair<Int, String?>, MutableList<LogRow>>()
        for (row in rows) grouped.getOrPut(row.kilometers.value to row.date?.value) { mutableListOf() } += row
        val repeatedHeaderSets = grouped.values
            .map { group -> group.mapNotNull { it.header }.toSet() }
            .filter { it.size >= 2 }
            .groupingBy { it }
            .eachCount()
        val mergedDescriptions = mutableMapOf<Pair<Int, String?>, String>()
        for ((key, group) in grouped) {
            val headerSet = group.mapNotNull { it.header }.toSet()
            if (headerSet.size >= 2 && (repeatedHeaderSets[headerSet] ?: 0) >= 2) {
                mergedDescriptions[key] = group.map { it.description }.distinct().joinToString(" + ")
            }
        }
        val result = mutableListOf<CandidateMaintenance>()
        val emitted = mutableSetOf<Pair<Int, String?>>()
        for (row in rows) {
            val key = row.kilometers.value to row.date?.value
            val mergedDescription = mergedDescriptions[key]
            if (mergedDescription != null) {
                if (!emitted.add(key)) continue
                result += logMaintenance(row, mergedDescription, assignments, clusterCount)
            } else {
                result += logMaintenance(row, row.description, assignments, clusterCount)
            }
        }
        return result
    }

    private fun logMaintenance(
        row: LogRow,
        description: String,
        assignments: List<Pair<SourceSpan, Int>>,
        clusterCount: Int,
    ): CandidateMaintenance {
        val vehicleIndex = if (clusterCount == 1) 0 else nearestVehicleIndex(assignments, row.kilometers.span)
        return CandidateMaintenance(
            description = ExtractedField(description, ExtractionConfidence.MEDIUM, row.descriptionSpan),
            date = row.date,
            kilometers = row.kilometers,
            cost = null,
            vehicleIndex = vehicleIndex,
        )
    }

    // --- Quantity on an invoice line ----------------------------------------------------------

    private data class QuantityMatch(val quantity: Occurrence, val unit: Occurrence?)

    /**
     * Quantity (and optional unit) written next to a part phrase. Invoice lines usually put it
     * after the description ("FILTRO ACEITE ... 2 ud"), notes often before ("4 bujías"), so both
     * sides are checked and the search never crosses into the next part phrase.
     */
    private fun findQuantityNear(span: SourceSpan, stopAt: SourceSpan?): QuantityMatch? {
        val afterEnd = minOf(
            (span.end + QUANTITY_AFTER_WINDOW).coerceAtMost(norm.length),
            stopAt?.start ?: norm.length,
        )
        if (afterEnd > span.end) {
            findQuantityIn(norm.substring(span.end, afterEnd), span.end, preferLast = false)?.let { return it }
        }
        val beforeStart = (span.start - QUANTITY_WINDOW).coerceAtLeast(0)
        if (beforeStart < span.start) {
            findQuantityIn(norm.substring(beforeStart, span.start), beforeStart, preferLast = true)?.let { return it }
        }
        return null
    }

    private fun findQuantityIn(region: String, regionStart: Int, preferLast: Boolean): QuantityMatch? {
        val matches = QUANTITY_PATTERN.findAll(region).toList()
        val match = (if (preferLast) matches.lastOrNull() else matches.firstOrNull()) ?: return null
        // Either "x4" (number trailing) or "4 ud" (number leading, unit trailing).
        val hasLeadingX = match.groups[1] != null
        val valueRange = if (hasLeadingX) match.trailingGroupRange(1) else match.leadingGroupRange(2)
        val valueSpan = SourceSpan(regionStart + valueRange.first, regionStart + valueRange.last + 1)
        val quantity = parseDecimal(match.groupValues[if (hasLeadingX) 1 else 2]) ?: return null
        val unit = match.groups[3]?.let { group ->
            val unitRange = match.trailingGroupRange(3)
            Occurrence(
                SourceSpan(regionStart + unitRange.first, regionStart + unitRange.last + 1),
                normalizeUnit(group.value),
                ExtractionConfidence.MEDIUM,
            )
        }
        return QuantityMatch(Occurrence(valueSpan, quantity.toString(), ExtractionConfidence.MEDIUM), unit)
    }

    private fun normalizeUnit(raw: String): String = when (raw) {
        "uds", "unidad", "unidades" -> "ud"
        "l", "litro", "litros" -> "L"
        else -> raw
    }
}

/** Parses a decimal with either separator style ("1.234,56" -> 1234.56). */
private fun parseDecimal(raw: String): Double? {
    val cleaned = when {
        raw.contains(',') && raw.contains('.') -> {
            // The last separator is the decimal point; drop the others.
            if (raw.lastIndexOf(',') > raw.lastIndexOf('.')) raw.replace(".", "").replace(",", ".") else raw.replace(",", "")
        }

        raw.contains(',') -> raw.replace(',', '.')
        else -> raw
    }
    return cleaned.toDoubleOrNull()
}

// --- Regexes and keyword tables -----------------------------------------------------------------

    // Applied to the normalized (lowercase) text; I, O and Q are not valid VIN characters.
    private val VIN_REGEX = Regex("[a-hj-npr-z0-9]{17}")

/** ISO 3779 character values (I, O and Q are not allowed in a VIN). */
private val VIN_CHAR_VALUES: Map<Char, Int> = mapOf(
    'A' to 1, 'B' to 2, 'C' to 3, 'D' to 4, 'E' to 5, 'F' to 6, 'G' to 7, 'H' to 8,
    'J' to 1, 'K' to 2, 'L' to 3, 'M' to 4, 'N' to 5, 'P' to 7, 'R' to 9, 'S' to 2,
    'T' to 3, 'U' to 4, 'V' to 5, 'W' to 6, 'X' to 7, 'Y' to 8, 'Z' to 9
) + ('0'..'9').associateWith { it - '0' }

/** ISO 3779 position weights (16 positions; the 9th is the checksum). */
private val VIN_WEIGHTS = intArrayOf(8, 7, 6, 5, 4, 3, 2, 10, 9, 8, 7, 6, 5, 4, 3, 2)

private val PLATE_REGEX = Regex(
    "\\d{4} ?[a-z]{3}|\\d{3} ?[a-z]{3}|\\d{2} ?[a-z]{4}|\\d{1} ?[a-z]{5}"
)

/**
 * Ambiguous (short) plate shapes are only accepted when a "matrícula" label sits this close
 * before them, so prose like "2 VECES" does not become a plate.
 */
private const val PLATE_LABEL_WINDOW = 30

/** Letter parts that are engine-tech tokens, not plates ("motor 2017 TDI"). */
private val PLATE_LETTER_STOPLIST = setOf("tdi", "tsi", "tfsi", "gtd", "gtdi", "lpg", "cng", "v6", "v8", "v12")

// Digit 0 read as the letter O ("1OW30") must still match; the value is repaired afterwards.
private val OIL_SPEC_REGEX = Regex("[0-9o]{1,2}w[0-9o]{2,3}")

private val TIRE_SIZE_REGEX = Regex("\\d{3}/\\d{2}\\s?r\\d{2}")

/** Bulb keyword anchors ("Bombillas HI2R", "Luces delanteras H7"); the code follows on the line. */
private val BULB_KEYWORDS = listOf("bombillas", "bombilla", "luces")

/**
 * Common bulb codes ("H4", "H7", "H11", "HIR2"/OCR "HI2R", "HB3", "LED"...). Each alternative
 * requires a real prefix, so a bare number after "luces"/"bombillas" ("Bombillas 2") is not a
 * code. The `hi\d[a-z]` alternative covers the OCR-scrambled "HI2R" form of "HIR2".
 */
private val BULB_CODE_REGEX = Regex(
    "h(?:i?r)?\\d{1,2}[a-z]?|hi\\d[a-z]|hb\\d|led|d[1-4][sr]|w5w|p21w|py21w"
)

/** Battery capacity in Ah, with optional technology prefix and cold-cranking amps in A. */
private val BATTERY_SPEC_REGEX = Regex("(?:agm|efb|gel|plomo|litio)?[\\s-]*\\d{2,3}\\s?ah(?:\\s+\\d{3,4})?")

/** Coolant keywords; "liquido refrigerante" is covered by "refrigerante". */
private val COOLANT_KEYWORDS = listOf("refrigerante", "anticongelante")

/** VW coolant standards ("G11", "G12", "G12+", "G13"). */
private val COOLANT_SPEC_REGEX = Regex("g1[123](?:\\+\\+)?")

/** "delanteras" / "traseras" markers; word-bounded so "atrás" is not a rear marker. */
private val FRONT_SIDE_REGEX = Regex("\\bdelant")
private val REAR_SIDE_REGEX = Regex("\\btras")

private val YEAR_REGEX = Regex("(?:19|20)\\d{2}")

private val ENGINE_DETAIL_REGEX = Regex("[1-6][.,]\\d(?: ?(tdi|tsi|tfsi|gtd|gtdi|lpg|cng))?")

private val DATE_SLASH_REGEX = Regex("\\d{1,2}/\\d{1,2}/\\d{4}")

/** "Próxima ITV 31/12/2027" (or just "Próxima 31/12/2027"); the date feeds nextItvDate. */
private val PROXIMA_ITV_REGEX = Regex("proxima\\s*(?:itv\\s*:?\\s*)?(\\d{1,2}/\\d{1,2}/\\d{4})")
private val DATE_ISO_REGEX = Regex("\\d{4}-\\d{2}-\\d{2}")
private val DATE_MONTH_REGEX = Regex(
    "(enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|octubre|noviembre|diciembre)(?: ?(?:de )?(?:19|20)\\d{2})?"
)

/**
 * [MatchGroup.range] is JVM-only — Kotlin/JS exposes just [MatchGroup.value] — so capture spans
 * are derived from the match range instead. Every regex below anchors its capture group at one
 * end of the match, which makes the offset exact rather than a search.
 */
private fun MatchResult.leadingGroupRange(index: Int): IntRange {
    val length = groupValues[index].length
    return range.first until range.first + length
}

private fun MatchResult.trailingGroupRange(index: Int): IntRange {
    val length = groupValues[index].length
    return (range.last - length + 1)..range.last
}

private val KM_NUMERIC_REGEX = Regex("(\\d{1,3}(?:[.,]\\d{3})+|\\d+)\\s*(?:km|kms|kilometros)")
private val KM_UNIT_REGEX = Regex("kilometros|kms|km")

private val COST_REGEX = Regex(
    "(\\d{1,3}(?:[.,]\\d{3})+(?:[.,]\\d{1,2})?|\\d+(?:[.,]\\d{1,2})?) ?(€|euros?|eur)"
)

private val REFERENCE_REGEX = Regex("ref(?:erencia)?\\.?:? *([a-z0-9][a-z0-9/\\-.]{2,})")

/**
 * Strong "this is a workshop document" markers (checked with word boundaries). "presupuesto"
 * and "mano de obra" are deliberately absent: prose like "me dieron un presupuesto por la mano
 * de obra" must not switch to invoice mode and collapse the note's maintenance actions.
 */
private val INVOICE_STRONG_MARKERS = listOf(
    "factura", "orden de reparacion", "base imponible", "total a pagar",
)

/** Quantity before a part phrase: "x4", "4 ud", "2 L", "3 unidades". */
private val QUANTITY_PATTERN = Regex(
    "(?:x ?(\\d+(?:[.,]\\d+)?)|(\\d+(?:[.,]\\d+)?) ?(x|ud|uds|unidad(?:es)?|l|litros?|kg|m))"
)

/** A number as written in a label value ("4", "2.2", "1.234,56"). */
private val LABELLED_AMOUNT_REGEX = Regex("(\\d{1,3}(?:[.,]\\d{3})+(?:[.,]\\d{1,2})?|\\d+(?:[.,]\\d{1,2})?)")

private val OIL_BRANDS = listOf(
    "castrol", "mobil", "shell", "total", "repsol", "valvoline", "fuchs",
    "aral", "elf", "motul", "penrite", "kixx", "idemitsu"
)

private val KNOWN_PARTS_BRANDS = setOf(
    "mann", "bosch", "brembo", "philips", "ngk", "castrol", "mobil", "shell",
    "total", "repsol", "michelin", "continental", "goodyear", "pirelli", "varta",
    "mahle", "sachs", "valeo", "denso", "hella", "amortec"
)

private val FUEL_KEYWORDS = listOf("gasolina", "diesel", "hibrido", "electrico", "gpl", "gnc", "cng")

private val VEHICLE_TYPE_KEYWORDS = listOf(
    "motocicleta" to "motorcycle",
    "moto" to "motorcycle",
    "furgoneta" to "van",
    "furgon" to "van",
    "camion" to "truck",
    "coche" to "car",
    "turismo" to "car"
)

/** European motorcycle category marks ("L3e", "L1e"...), matched as a whole token. */
private val MOTORCYCLE_CATEGORY_REGEX = Regex("l[1-7]e?")

/** Commercial model shape on a data sheet: letter prefix, digits, optional alphanumeric suffix. */
private val MODEL_TOKEN_REGEX = Regex("[A-Z]{1,4}[0-9]{2,}[A-Z0-9]*")

private data class PartKeyword(val phrase: String, val category: String, val type: String?)

/** Catalog keyword table (normalized phrases), longest first so specific phrases win. */
private val PART_KEYWORDS = listOf(
    PartKeyword("pastillas de freno delanteras", "brakes", "brake_pads_front"),
    PartKeyword("pastillas de freno traseras", "brakes", "brake_pads_rear"),
    PartKeyword("filtro de habitaculo", "filters", "cabin_filter"),
    PartKeyword("aceite de transmision", "oil", "transmission_oil"),
    PartKeyword("liquido de frenos", "brakes", "brake_fluid"),
    PartKeyword("pastillas de freno", "brakes", "all_brake_pads"),
    PartKeyword("filtro de aceite", "filters", "oil_filter"),
    PartKeyword("filtro de aire", "filters", "air_filter"),
    PartKeyword("filtro de gasoil", "filters", "fuel_filter"),
    PartKeyword("disco de freno", "brakes", "brake_discs"),
    PartKeyword("discos de freno", "brakes", "brake_discs"),
    PartKeyword("neumatico nuevo", "tires", "tire"),
    PartKeyword("aceite de motor", "oil", "engine_oil"),
    PartKeyword("filtro de polen", "filters", "cabin_filter"),
    PartKeyword("bateria nueva", "battery", "battery_12v"),
    // Invoice wording drops prepositions and shortens part names ("FILTRO ACEITE 2 ud").
    PartKeyword("correa de distribucion", "other", null),
    PartKeyword("correa distribucion", "other", null),
    PartKeyword("pastillas freno delanteras", "brakes", "brake_pads_front"),
    PartKeyword("pastillas freno traseras", "brakes", "brake_pads_rear"),
    PartKeyword("juego de pastillas", "brakes", "all_brake_pads"),
    PartKeyword("juego pastillas", "brakes", "all_brake_pads"),
    PartKeyword("filtro de combustible", "filters", "fuel_filter"),
    PartKeyword("filtro combustible", "filters", "fuel_filter"),
    PartKeyword("filtro aceite", "filters", "oil_filter"),
    PartKeyword("filtro aire", "filters", "air_filter"),
    PartKeyword("filtro gasoil", "filters", "fuel_filter"),
    PartKeyword("filtro habitaculo", "filters", "cabin_filter"),
    PartKeyword("filtro polen", "filters", "cabin_filter"),
    PartKeyword("aceite caja cambios", "oil", "transmission_oil"),
    PartKeyword("valvulina", "oil", "transmission_oil"),
    PartKeyword("pastillas freno", "brakes", "all_brake_pads"),
    PartKeyword("discos freno", "brakes", "brake_discs"),
    PartKeyword("disco freno", "brakes", "brake_discs"),
    PartKeyword("liquido frenos", "brakes", "brake_fluid"),
    PartKeyword("bomba de agua", "other", null),
    PartKeyword("kit distribucion", "other", null),
    PartKeyword("aceite motor", "oil", "engine_oil"),
    PartKeyword("bujias de encendido", "other", null),
    PartKeyword("neumaticos", "tires", "tire"),
    PartKeyword("neumatico", "tires", "tire"),
    PartKeyword("llantas", "tires", "tire"),
    PartKeyword("baterias", "battery", "battery_12v"),
    PartKeyword("bateria", "battery", "battery_12v"),
    PartKeyword("bujias", "other", null),
    PartKeyword("bujia", "other", null),
    PartKeyword("llanta", "tires", "tire"),
    PartKeyword("filtro", "filters", null)
).sortedByDescending { it.phrase.length }

private data class ActionKeyword(val phrase: String, val specific: Boolean)

/** Maintenance action phrases (normalized), longest first. */
private val MAINTENANCE_ACTIONS = listOf(
    ActionKeyword("cambio de aceite", true),
    ActionKeyword("cambie el aceite", true),
    ActionKeyword("cambio de pastillas", true),
    ActionKeyword("cambio de bujias", true),
    ActionKeyword("cambio de neumaticos", true),
    ActionKeyword("cambio de llantas", true),
    ActionKeyword("cambio de filtro", true),
    ActionKeyword("cambio de bateria", true),
    ActionKeyword("revision", false),
    ActionKeyword("itv", false),
    ActionKeyword("mantenimiento", false)
).sortedByDescending { it.phrase.length }
